import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useAdsHidden } from '../../hooks/useAdsHidden';

/**
 * ニュース一覧に混ぜるインフィード広告。
 *
 * カードと同じ寸法・同じ枠で並べるが、**記事と見分けがつく必要がある**ため
 * ラベルを必ず出す。ラベル文言は AdSense のプログラムポリシーで
 * 「広告」「スポンサーリンク」相当のものに限られている
 * （「PR」だけでは要件を満たさない可能性が高い）。
 * 変えるときは AD_LABEL だけ差し替えれば済むようにしてある。
 *
 * PROプラン以上には出さない（useAdsHidden）。
 */
const AD_LABEL = '広告';

// インフィード広告は AdSense 管理画面で「インフィード広告」ユニットを作ると
// スロットIDとレイアウトキーが発行される。既定値は未設定なので、
// 発行された値を .env に入れる（入るまでは何も描画しない）
const AD_CLIENT = 'ca-pub-6000060497265595';
const AD_SLOT = process.env.REACT_APP_ADSENSE_NEWS_INFEED_SLOT || '';
const AD_LAYOUT_KEY = process.env.REACT_APP_ADSENSE_NEWS_INFEED_LAYOUT_KEY || '';

const NewsFeedAd: React.FC = () => {
  const adsHidden = useAdsHidden();
  const ref = useRef<HTMLDivElement>(null);
  // StrictMode の二重実行や再レンダリングで同じ枠に二度 push すると
  // AdSense 側でエラーになるため、1インスタンス1回に固定する
  const pushed = useRef(false);

  useEffect(() => {
    if (adsHidden || !AD_SLOT || pushed.current || !ref.current) return;

    const ins = document.createElement('ins');
    ins.className = 'adsbygoogle';
    ins.style.display = 'block';
    ins.style.width = '100%';
    ins.setAttribute('data-ad-client', AD_CLIENT);
    ins.setAttribute('data-ad-slot', AD_SLOT);
    ins.setAttribute('data-ad-format', 'fluid');
    if (AD_LAYOUT_KEY) {
      ins.setAttribute('data-ad-layout-key', AD_LAYOUT_KEY);
    }
    ref.current.appendChild(ins);

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch (error) {
      console.error('Adsense error:', error);
    }
  }, [adsHidden]);

  // 未設定のうちは枠ごと出さない。空欄が並ぶより出さないほうがまし
  if (adsHidden || !AD_SLOT) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'rgba(255,255,255,0.02)',
        border: '1px dashed rgba(255,255,255,0.12)',
        minHeight: 220,
      }}
    >
      <Typography
        sx={{
          px: 1.25, py: 0.5,
          // このプロジェクトは 1rem = 20px (index.css)。0.52rem ≒ 10px
          fontSize: { xs: '0.5rem', sm: '0.55rem' },
          letterSpacing: '.08em',
          color: 'rgba(255,255,255,0.4)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {AD_LABEL}
      </Typography>
      <Box ref={ref} sx={{ flex: 1, minHeight: 0 }} />
    </Box>
  );
};

export default NewsFeedAd;
